import { Ref, ref } from 'vue';
import { course, language } from '.';

export interface Progress {
  pct: number;
  done: string[];
}

export interface TrainStepProgress {
  completed: boolean;
}
export interface StoryProgress {
  completed: boolean;
  trainStepsCompleted: boolean;
  progress: number;
  trainSteps: TrainStepProgress[];
}

export interface CourseProgress {
  overallProgress: number;
  totalStories: number;
  completedStories: number;
  stories: StoryProgress[];
}

const COURSE_PROGRESS_STORAGE_KEY = 'courseProgress';
const DEFAULT_PROGRESS = {
  overallProgress: 0,
  totalStories: 0,
  completedStories: 0,
  stories: []
};

export default () => {
  const { curriculum } = course.state;
  const { langForeign, langNative } = language.state;

  const courseProgress = ref<CourseProgress>(JSON.parse(JSON.stringify(DEFAULT_PROGRESS)));
  const courseProgressLangs: Ref<Record<string, CourseProgress>> = ref({});

  const getLangKey = () => {
    return `${langForeign.value}_${langNative.value}`;
  };

  // Function to save courseProgress to localStorage
  const saveCourseProgressToLocalStorage = () => {
    try {
      if (courseProgress.value.stories.length === 0) {
        console.warn('Course progress is empty; not saving to localStorage.');
        return;
      }
      console.log('Saving course progress to localStorage:', courseProgress.value);
      courseProgressLangs.value[getLangKey()] = courseProgress.value;
      localStorage.setItem(COURSE_PROGRESS_STORAGE_KEY, JSON.stringify(courseProgressLangs.value));
    } catch (error) {
      console.error('Failed to save course progress to localStorage:', error);
    }
  };

  // Function to load courseProgress from localStorage
  const loadCourseProgressFromLocalStorage = () => {
    if (langForeign.value && langNative.value) {
      const stored = localStorage.getItem(COURSE_PROGRESS_STORAGE_KEY);
      if (stored) {
        courseProgressLangs.value = JSON.parse(stored);
        if (Object.keys(courseProgressLangs.value).includes(getLangKey())) {
          courseProgress.value = courseProgressLangs.value[getLangKey()];
        } else {
          courseProgress.value = JSON.parse(JSON.stringify(DEFAULT_PROGRESS));
        }
        return true;
      }
    } else {
      console.warn('Language settings not defined; cannot load course progress from localStorage.');
    }
    return false;
  };

  // Function to initialize courseProgress with data from localStorage or defaults
  const initCourseProgressState = (): Promise<void> => {
    return new Promise((resolve) => {
      const loaded = loadCourseProgressFromLocalStorage();
      if (loaded) {
        console.log('Course progress loaded from localStorage:');
        curriculum.value.forEach((story, sIdx) => {
          const trainSteps = story.trainSteps.map((tS) => ({ completed: false }));
          if (!courseProgress.value.stories[sIdx]) {
            courseProgress.value.stories.push({
              completed: false,
              trainStepsCompleted: false,
              progress: 0,
              trainSteps
            });
          }
        });
      } else {
        console.log('No course progress in localStorage.');
        // Initialize with default empty structure
        const _courseProgress: CourseProgress = {
          overallProgress: 0,
          totalStories: curriculum.value.length,
          completedStories: 0,
          stories: []
        };
        curriculum.value.forEach((story) => {
          const trainSteps = story.trainSteps.map((tS) => ({ completed: false }));
          _courseProgress.stories.push({
            completed: false,
            trainStepsCompleted: false,
            progress: 0,
            trainSteps
          });
        });
        courseProgress.value = _courseProgress;
        console.log('Initialized new course progress:', _courseProgress);
        updateCourseProgress();
      }
      resolve();
    });
  };

  // Calculate and update course progress data
  const calculateCourseProgress = () => {
    let completedStoriesCount = 0;
    courseProgress.value.stories.forEach((storyProgress) => {
      const completedTrainSteps = storyProgress.trainSteps.filter((tS) => tS.completed);
      const trCompleted = completedTrainSteps.length === storyProgress.trainSteps.length;
      storyProgress.trainStepsCompleted = trCompleted;
      storyProgress.progress = parseInt(
        ((completedTrainSteps.length / storyProgress.trainSteps.length) * 100).toString()
      );
      if (trCompleted && storyProgress.completed) {
        completedStoriesCount += 1;
      }
    });
    courseProgress.value.completedStories = completedStoriesCount;
    courseProgress.value.overallProgress = parseInt(
      ((completedStoriesCount / courseProgress.value.stories.length) * 100).toString()
    );
  };

  const updateCourseProgress = () => {
    calculateCourseProgress();
    console.log('Course progress updated:', courseProgress.value);
    saveCourseProgressToLocalStorage();
  };

  // Function to mark a specific step as completed
  const markStoryCompleted = (storyIdx: number) => {
    courseProgress.value.stories[storyIdx].completed = true;
    console.log(`Story ${storyIdx} marked as completed.`);
    updateCourseProgress();
  };

  // Function to mark a specific training step as completed
  const markTrainStepCompleted = (storyIdx: number, trainStepIndex: number) => {
    courseProgress.value.stories[storyIdx].trainSteps[trainStepIndex].completed = true;
    // Recalculate progress
    updateCourseProgress();
  };

  return {
    state: { courseProgress },
    funcs: {
      initCourseProgressState,
      calculateCourseProgress,
      updateCourseProgress,
      markTrainStepCompleted,
      markStoryCompleted,
      saveCourseProgressToLocalStorage,
      loadCourseProgressFromLocalStorage
    }
  };
};
